import { type AccessState, hasSubscription, parseAccessState } from '@lingoping/core';
import { getSupabase, isSupabaseConfigured } from '../../config/supabase.js';
import { saveAccess } from '../../services/access.js';
import { StorageManager } from '../../storage/index.js';

/**
 * The dashboard's calls to the billing Edge Functions (Docs/plans/SUBSCRIPTION_PLAN.md, section
 * 11), all made as the signed-in learner. The server decides everything; these only ask.
 */

export class BillingError extends Error {
  constructor(
    message: string,
    /** The HTTP status when the server answered (409: already subscribed). */
    readonly status?: number
  ) {
    super(message);
    this.name = 'BillingError';
  }
}

/** The status of a failed function call, when the server answered. */
function statusOf(error: unknown): number | undefined {
  const context = (error as { context?: unknown } | null)?.context;
  const status = (context as { status?: unknown } | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
}

async function call<T>(name: string, body?: unknown, method: 'GET' | 'POST' = 'POST'): Promise<T> {
  const { data, error } = await getSupabase().functions.invoke<T>(name, {
    method,
    ...(method === 'POST' ? { body: body ?? {} } : {}),
  });
  if (error) throw new BillingError(error.message || `${name} failed`, statusOf(error));
  return data as T;
}

/** A Paddle transaction for the checkout, with the learner's remaining trial days built in. */
export async function createCheckout(): Promise<{ transaction_id: string }> {
  const data = await call<{ transaction_id?: unknown }>('billing-checkout');
  if (typeof data?.transaction_id !== 'string' || !data.transaction_id) {
    throw new BillingError('No transaction came back');
  }
  return { transaction_id: data.transaction_id };
}

/** The address of Paddle's customer portal (change card, invoices). */
export async function openBillingPortalUrl(): Promise<string> {
  const data = await call<{ url?: unknown }>('billing-portal');
  if (typeof data?.url !== 'string' || !/^https:\/\//.test(data.url)) {
    throw new BillingError('No portal address came back');
  }
  return data.url;
}

export interface CancelResult {
  ok: boolean;
  received_at: string;
}

function parseCancelResult(data: unknown): CancelResult {
  const result = data as { ok?: unknown; received_at?: unknown } | null;
  if (result?.ok !== true || typeof result.received_at !== 'string') {
    throw new BillingError('The cancellation was not confirmed');
  }
  return { ok: true, received_at: result.received_at };
}

/** Cancels the signed-in learner's subscription at the end of the paid period. */
export async function cancelOwnSubscription(): Promise<CancelResult> {
  return parseCancelResult(await call<unknown>('cancel-subscription', {}));
}

/**
 * What the public cancel form sends (§312k BGB): who, the kind of cancellation (with the reason
 * for an extraordinary one) and when it should take effect (`next_possible` or a day).
 */
export interface CancelRequest {
  email: string;
  name: string;
  kind: 'ordinary' | 'extraordinary';
  reason?: string;
  effective: 'next_possible' | string;
}

/** The public cancel form: by email address, no sign-in (§312k BGB). */
export async function cancelByEmail(request: CancelRequest): Promise<CancelResult> {
  const { reason, ...rest } = request;
  return parseCancelResult(
    await call<unknown>('cancel-subscription', {
      ...rest,
      ...(request.kind === 'extraordinary' && reason ? { reason } : {}),
    })
  );
}

export async function deleteOwnAccount(): Promise<void> {
  await call<unknown>('delete-account', { confirm: 'DELETE' });
}

/** The learner's data as a file the browser can save. */
export async function exportOwnData(): Promise<Blob> {
  const data = await call<unknown>('export-data');
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data;
  return new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
}

/** Saves the export on the learner's computer. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function getReminderEmails(): Promise<boolean> {
  const data = await call<{ reminders?: unknown }>('email-preferences', undefined, 'GET');
  return data?.reminders !== false;
}

export async function setReminderEmails(reminders: boolean): Promise<void> {
  await call<unknown>('email-preferences', { reminders });
}

/**
 * The server's current answer (RPC `my_access`), noted on this device so every screen redraws.
 * Null when it cannot be asked (signed out, offline, not configured).
 */
export async function fetchMyAccess(): Promise<AccessState | null> {
  try {
    if (!isSupabaseConfigured()) return null;
    const supabase = getSupabase();
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) return null;
    const { data, error } = await supabase.rpc('my_access');
    if (error) return null;
    const userId = await StorageManager.getCurrentUserId();
    return userId ? await saveAccess(userId, data) : parseAccessState(data);
  } catch (err) {
    console.warn('[LingoPing] Could not fetch the access state:', err);
    return null;
  }
}

export interface PollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * After a checkout the subscription arrives through Paddle's webhook a moment later: asks again
 * every couple of seconds (up to ~30) until the learner has a subscription. Returns the new
 * state, or null if it did not show up in time.
 */
export async function waitForSubscription(
  fetchAccess: () => Promise<AccessState | null>,
  { intervalMs = 2000, timeoutMs = 30_000, sleep = defaultSleep }: PollOptions = {}
): Promise<AccessState | null> {
  for (let waited = 0; waited <= timeoutMs; waited += intervalMs) {
    const access = await fetchAccess();
    if (hasSubscription(access)) return access;
    if (waited + intervalMs > timeoutMs) break;
    await sleep(intervalMs);
  }
  return null;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
